import { useQuery } from '@tanstack/react-query';
import {
  Cpu, ExternalLink, AlertTriangle, CheckCircle, XCircle, CircleDashed,
  Settings, RefreshCw,
} from 'lucide-react';
import { inference, type GatewayNode, type GatewayStatus } from '../api';
import MetrikPanel from '../components/MetrikPanel';

/**
 * Der ai-loadbalancer, gesehen vom Provisioning Center.
 *
 * BEWUSST SCHMAL. Der Gateway hat eine eigene Oberfläche, in der man
 * konfiguriert, Schlüssel ausstellt und Benchmarks fährt. Sie hier nachzubauen
 * hiesse, zwei halbe Oberflächen zu pflegen, die auseinanderlaufen — und die
 * zweite wäre immer die veraltete.
 *
 * Diese Seite beantwortet, was jemand im PC wissen will: laufen die Knoten,
 * welche Modelle liegen bereit, klemmt etwas. Für alles Weitere steht ein
 * Verweis dorthin, wo es hingehört.
 */

const ZUSTAND: Record<GatewayNode['status'], { text: string; klasse: string; Symbol: typeof CheckCircle }> = {
  ONLINE:   { text: 'läuft',       klasse: 'text-emerald-700 bg-emerald-50 border-emerald-200', Symbol: CheckCircle },
  DEGRADED: { text: 'angeschlagen', klasse: 'text-amber-700 bg-amber-50 border-amber-200',      Symbol: AlertTriangle },
  OFFLINE:  { text: 'offline',     klasse: 'text-rose-700 bg-rose-50 border-rose-200',          Symbol: XCircle },
  UNKNOWN:  { text: 'unbekannt',   klasse: 'text-slate-500 bg-slate-50 border-slate-200',       Symbol: CircleDashed },
};

function ZustandsMarke({ status }: { status: GatewayNode['status'] }) {
  const z = ZUSTAND[status] ?? ZUSTAND.UNKNOWN;
  return (
    <span className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-xs ${z.klasse}`}>
      <z.Symbol size={12} /> {z.text}
    </span>
  );
}

function bytes(n?: number | null): string {
  if (!n) return '—';
  const e = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0; let w = n;
  while (w >= 1024 && i < e.length - 1) { w /= 1024; i++; }
  return `${w.toFixed(w < 10 && i > 0 ? 1 : 0)} ${e[i]}`;
}

export default function InferencePage() {
  const { data, isLoading, refetch, isFetching } = useQuery<GatewayStatus>({
    queryKey: ['inference-status'],
    queryFn: inference.status,
    // Alle 30 s: der Gateway prüft seine Knoten selbst im Takt, häufiger zu
    // fragen zeigt nur dieselbe Antwort noch einmal.
    refetchInterval: 30_000,
  });

  if (isLoading) {
    return <p className="text-sm text-slate-400">wird geladen…</p>;
  }

  // ── Nicht eingerichtet ist kein Fehler ────────────────────────────────────
  //
  // Wer die Zugangsdaten noch nicht hinterlegt hat, soll das lesen und nicht
  // „Gateway antwortet nicht": das eine ist eine Einrichtungsaufgabe, das
  // andere eine Störung. Sie zu verwechseln kostet eine halbe Stunde Suche.
  if (data && !data.eingerichtet) {
    return (
      <div className="space-y-4">
        <h1 className="flex items-center gap-2 text-xl font-semibold text-slate-900">
          <Cpu size={20} /> KI-Gateway
        </h1>
        <div className="rounded-xl border border-slate-200 bg-surface p-6">
          <p className="mb-3 text-sm text-slate-700">Noch nicht eingerichtet.</p>
          <p className="mb-4 text-sm text-slate-500">{data.hinweis}</p>
          <a
            href="/settings"
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm text-white hover:bg-brand-700"
          >
            <Settings size={14} /> Zu den Einstellungen
          </a>
        </div>
      </div>
    );
  }

  const k = data?.kennzahlen;
  const nodes = data?.nodes ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="flex items-center gap-2 text-xl font-semibold text-slate-900">
          <Cpu size={20} /> KI-Gateway
        </h1>
        {data?.version && (
          <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-500">
            v{data.version}
          </span>
        )}
        <button
          onClick={() => refetch()}
          className="rounded-lg border border-slate-300 p-1.5 hover:bg-slate-50"
          title="Jetzt neu laden"
        >
          <RefreshCw size={13} className={isFetching ? 'animate-spin' : ''} />
        </button>

        <div className="ml-auto flex gap-2">
          {data?.adressen?.oberflaeche && (
            <a
              href={data.adressen.oberflaeche}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50"
            >
              Verwalten <ExternalLink size={13} />
            </a>
          )}
        </div>
      </div>

      {/*
        Ein Fehler des Gateways wird durchgereicht, nicht durch ein eigenes
        „Fehler beim Abruf" ersetzt. Seine Meldungen sagen, was zu tun ist.
      */}
      {data && !data.erreichbar && (
        <p className="flex items-start gap-2 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-800">
          <AlertTriangle size={15} className="mt-0.5 shrink-0" />
          <span>
            Der Gateway ist nicht erreichbar: {data.fehler ?? 'kein Grund genannt'}
            {data.adressen?.api && (
              <span className="block text-xs text-rose-600">{data.adressen.api}</span>
            )}
          </span>
        </p>
      )}

      {k && (
        <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {[
            ['Knoten', k.knoten, ''],
            ['laufen', k.online, k.online > 0 ? 'text-emerald-700' : 'text-slate-400'],
            ['auffällig', k.auffaellig, k.auffaellig > 0 ? 'text-amber-700' : 'text-slate-400'],
            ['Modelle', k.modelle, ''],
            ['Anfragen', k.laufendeAnfragen, ''],
            ['abgeschaltet', k.abgeschaltet + k.wirdGeleert, k.abgeschaltet + k.wirdGeleert > 0 ? 'text-slate-700' : 'text-slate-400'],
          ].map(([label, wert, farbe]) => (
            <div key={label as string} className="rounded-xl border border-slate-200 bg-surface p-3">
              <p className="text-xs uppercase tracking-wide text-slate-400">{label}</p>
              <p className={`text-2xl font-semibold ${farbe || 'text-slate-800'}`}>{wert as number}</p>
            </div>
          ))}
        </div>
      )}

      {/*
        Die Kacheln oben zeigen das Jetzt, und genau das war die Lücke: ein Node,
        der heute Nacht eine Stunde weg war, ist dort nicht zu sehen. Der
        Verlauf steht deshalb direkt darunter und nicht auf einer eigenen Seite.
      */}
      <div className="rounded-xl border border-slate-200 bg-surface p-4">
        <MetrikPanel titel="KI-Gateway" alarmPraefix="Inferenz" />
      </div>

      {nodes.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-surface p-6 text-sm text-slate-500">
          Kein Knoten eingetragen. Knoten werden in der Oberfläche des Gateways gepflegt.
        </div>
      ) : (
        nodes.map((n) => (
          <article key={n.id} className="rounded-xl border border-slate-200 bg-surface p-4">
            <div className="flex flex-wrap items-center gap-3">
              <ZustandsMarke status={n.status} />
              <h2 className="font-semibold text-slate-800">{n.name}</h2>
              <code className="text-xs text-slate-500">{n.base_url}</code>
              {n.ollama_version && (
                <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-500">
                  Ollama {n.ollama_version}
                </span>
              )}
              {n.group_name && (
                <span className="rounded bg-brand-50 px-1.5 py-0.5 text-xs text-brand-700">
                  {n.group_name}
                </span>
              )}
              {!n.enabled && (
                <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600">abgeschaltet</span>
              )}
              {n.draining && (
                <span className="rounded bg-amber-50 px-1.5 py-0.5 text-xs text-amber-700">wird geleert</span>
              )}
              <span className="ml-auto text-xs text-slate-500">{n.active_requests} laufend</span>
            </div>

            {n.last_error && (
              <p className="mt-2 rounded bg-amber-50 px-2 py-1 font-mono text-xs text-amber-800">
                {n.last_error}
              </p>
            )}

            <div className="mt-3 flex flex-wrap gap-1">
              {n.models.length === 0 ? (
                <span className="text-xs italic text-slate-400">keine Modelle gemeldet</span>
              ) : (
                n.models.map((m) => (
                  <span
                    key={m.name}
                    title={`${bytes(m.size_bytes)}${m.capabilities?.length ? ' · ' + m.capabilities.join(', ') : ''}`}
                    className={`rounded border px-1.5 py-0.5 text-xs ${
                      m.loaded
                        ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                        : 'border-slate-200 text-slate-600'
                    }`}
                  >
                    {m.name}
                  </span>
                ))
              )}
            </div>
          </article>
        ))
      )}

      {/*
        Der Verweis steht unten und nicht als Hauptknopf: die häufige Frage ist
        „läuft es", nicht „ich will etwas ändern".
      */}
      {data?.adressen?.api && (
        <p className="text-xs text-slate-400">
          Dienste sprechen <code className="text-slate-500">{data.adressen.api}/v1</code> direkt an —
          mit einem eigenen Schlüssel der Rolle CLIENT. Das Provisioning Center leitet keine
          Inferenz-Anfragen weiter; es liest nur den Zustand.
        </p>
      )}
    </div>
  );
}
